import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy,
  Zap,
  Target,
  BarChart3,
  Star,
  Award,
  Users,
  CheckCircle,
  Clock,
  TrendingUp,
  Shield,
  DollarSign,
  Lightbulb,
  ChevronRight,
  Medal,
  Crown,
  Sparkles,
  Brain,
  AlertTriangle,
  Eye,
  ClipboardList,
  Cpu,
  Database,
  GitMerge,
  Code,
  Rocket,
  PackageCheck,
  ShieldCheck,
  FileText
} from 'lucide-react';

interface EvaluationCriteria {
  id: string;
  name: string;
  description: string;
  weight: number;
  icon: React.ElementType;
}

interface IdeaScore {
  ideaId: string;
  scores: Record<string, number>;
  totalScore: number;
  rank: number;
  reasoning: string;
}

interface TournamentMatch {
  id: string;
  idea1: any;
  idea2: any;
  winner: string | null;
  criteria: string;
  round: number;
}

interface IdeaEvaluationPhaseProps {
  ideas: any[];
  onComplete: (results: any) => void;
}

// Hardcoded Top 10 Innovation Ideas Data
const TOP_10_INNOVATION_IDEAS = [
  {
    id: '561',
    title: 'Homomorphic Machine Learning Platform',
    description: 'ML platform that trains and executes models on encrypted data without ever decrypting sensitive information.',
    category: 'AI Enhancement',
    score: {
      ideaId: '561',
      scores: { innovation: 9.8, feasibility: 8.5, impact: 9.7, cost: 8.9, security: 9.9 },
      totalScore: 9.3,
      rank: 1,
      reasoning: 'Excels in Innovation and Security with revolutionary privacy-preserving capabilities'
    },
    difficulty: 'Very High',
    timeToImplement: '12-18 months',
    impact: 'Revolutionary',
    tags: ['Privacy', 'AI', 'Encryption', 'Machine Learning'],
    trizPrinciples: [15, 2, 40], // Dynamics, Taking out, Composite materials
    contradiction: 'Improving ML model accuracy vs. preserving complete data privacy',
    detailedData: {
      whyThisIsNumberOne: {
        title: 'Why This Is The #1 Privacy Innovation',
        points: [
          "**Breakthrough Technology Convergence:** Combines homomorphic encryption with machine learning to achieve what was previously impossible.",
          "**Massive Market Potential:** Unlocks AI in regulated industries like healthcare and finance, a $100B+ opportunity.",
          "**Solves Critical Industry Pain Points:** Enables HIPAA-compliant healthcare AI and fraud detection without data exposure."
        ]
      },
      revolutionaryFeatures: {
        core: [
          "**Encrypted Model Training:** Train neural networks on fully encrypted datasets without decryption",
          "**Secure Inference Engine:** Make predictions on encrypted queries with zero-knowledge processing",
          "**Federated Homomorphic Learning:** Multiple parties contribute encrypted data for collaborative training"
        ],
        advanced: [
          "Gradient Computation on Encrypted Data",
          "Secure Multi-Party Model Updates", 
          "Privacy-Preserving Model Evaluation",
          "Encrypted Feature Engineering"
        ]
      },
      futurePotential: {
        fiveYear: [
          "**Healthcare Revolution:** Global disease prediction models trained on encrypted patient data",
          "**Financial Transformation:** Global fraud detection without exposing transactions",
          "**Enterprise AI Democratization:** Small companies access AI trained on industry data"
        ],
        tenYear: [
          "**Regulatory Compliance:** Becomes the standard for AI in regulated industries",
          "**Global Collaboration:** International AI research without data sharing concerns",
          "**Democratic AI:** Individual privacy preserved while benefiting from collective intelligence"
        ]
      },
      commercialValue: {
        revenueStreams: [
          "Platform Licensing: $10M-100M annually per enterprise",
          "API Usage: Pay-per-encrypted-computation model",
          "Industry Solutions: Vertical-specific implementations"
        ],
        costSavings: [
          "Reduce compliance and regulatory costs by 60-80%",
          "Eliminate data breach exposure risks",
          "Faster deployment with no data anonymization delays"
        ],
        competitiveAdvantages: [
          "First-mover advantage with revolutionary technology",
          "Network effects as more participants increase model accuracy",
          "Regulatory alignment solving future privacy requirements today"
        ]
      },
      innovationHighlights: {
        technical: [
          "Novel algorithms reducing homomorphic computation overhead by 40%",
          "Parallel processing architectures for encrypted ML operations",
          "Optimized circuit designs for common ML models"
        ],
        industry: [
          "First commercially viable homomorphic ML implementation",
          "Enables cross-border AI training without data movement",
          "Built-in compliance with global privacy laws (GDPR, CCPA)"
        ]
      },
      comparisonToOthers: {
        vsQuantum: "**Immediate Market Need:** Privacy is urgent today, quantum threats are future-focused",
        vsZeroKnowledge: "**Transformative Impact:** Changes entire AI industry vs. verification use cases",
        vsFederated: "**Stronger Privacy:** True encryption vs. federated aggregation"
      },
      implementationRoadmap: [
        "Phase 1 (Months 1-6): Foundation - Core homomorphic encryption engine and basic ML operations",
        "Phase 2 (Months 7-12): Advanced ML - Deep learning support and real-world pilot with healthcare partner",
        "Phase 3 (Months 13-18): Platform - Full-featured ML platform, API ecosystem, and commercial launch"
      ],
      technicalDeepDive: {
        architecture: "A microservices architecture with homomorphic encryption at the core, supporting distributed computation across encrypted data sources.",
        techStack: {
          frontend: "React/TypeScript with privacy-focused UI components",
          backend: "Rust for performance-critical homomorphic operations",
          database: "Encrypted data stores with homomorphic query capabilities",
          infra: "Kubernetes with secure enclaves on AWS/GCP"
        },
        integrationPoints: [
          "Healthcare systems (EPIC, Cerner) with HIPAA compliance",
          "Financial platforms with PCI DSS integration",
          "Government systems with classified data handling"
        ]
      },
      resourceAllocation: {
        team: "1 Product Manager, 3 Senior Cryptography Engineers, 2 ML Engineers, 1 Frontend Engineer, 1 DevOps/SRE",
        budgetNote: "Initial budget should cover 18 months of runway for specialized team plus significant R&D costs"
      },
      trizFlow: {
        contradiction: "Improving ML model accuracy vs. preserving complete data privacy",
        principle: "Leveraging TRIZ Principles 15 (Dynamics), 2 (Taking out), 40 (Composite materials) to enable computation on encrypted data",
        implementation: "Develop homomorphic encryption algorithms that allow ML training without decryption, using dynamic key management",
        benefit: "Achieves full data privacy while maintaining ML accuracy and enabling new AI applications in regulated industries"
      },
      marketResearch: {
        marketSize: "$50B+ market creation within 10 years",
        competitorAnalysis: "No direct competitors for production-ready homomorphic ML; academic research stage globally",
        targetSegment: "Healthcare, Finance, Government, and Enterprise AI requiring privacy compliance",
        revenueModel: "Tiered SaaS with usage-based pricing for encrypted computations",
        timeToMarket: "12-18 months for MVP, 24 months for full platform",
        riskFactors: [
          "Computational overhead challenges for large-scale deployment",
          "Regulatory acceptance and standardization timeline",
          "Competition from tech giants entering the space"
        ]
      },
      keyPoints: [
        "Addresses the holy grail of privacy-preserving AI with $50B+ market potential",
        "First-mover advantage in commercially viable homomorphic ML",
        "Solves critical privacy-utility tradeoff that blocks AI adoption",
        "Enables entire new categories of AI applications in regulated industries"
      ],
      detailedAnalysis: "This idea ranks #1 due to its revolutionary approach to the privacy-utility tradeoff in AI. By enabling machine learning on encrypted data, it unlocks massive markets in healthcare, finance, and government that are currently blocked by privacy constraints. The technical innovation represents a genuine breakthrough, while the commercial potential is unprecedented."
    }
  },
  {
    id: '562',
    title: 'Quantum-Safe Blockchain Infrastructure',
    description: 'Next-generation blockchain with quantum-resistant cryptography and post-quantum security protocols.',
    category: 'Security & Compliance',
    score: {
      ideaId: '562',
      scores: { innovation: 9.2, feasibility: 8.8, impact: 9.5, cost: 8.4, security: 9.9 },
      totalScore: 9.1,
      rank: 2,
      reasoning: 'Excels in Security and Impact with future-proof cryptographic foundation'
    },
    difficulty: 'Very High',
    timeToImplement: '15-20 months',
    impact: 'Transformative',
    tags: ['Quantum', 'Blockchain', 'Cryptography', 'Security']
  },
  {
    id: '563',
    title: 'Zero-Knowledge Identity Verification',
    description: 'Identity verification system that proves identity without revealing any personal information.',
    category: 'Security & Compliance',
    score: {
      ideaId: '563',
      scores: { innovation: 9.4, feasibility: 8.2, impact: 9.0, cost: 8.8, security: 9.8 },
      totalScore: 9.0,
      rank: 3,
      reasoning: 'Excels in Innovation and Security with breakthrough privacy technology'
    },
    difficulty: 'High',
    timeToImplement: '10-14 months',
    impact: 'Revolutionary',
    tags: ['Zero-Knowledge', 'Identity', 'Privacy', 'Verification']
  },
  {
    id: '564',
    title: 'Autonomous Code Generation Engine',
    description: 'AI system that generates, tests, and deploys production-ready code from natural language requirements.',
    category: 'AI Enhancement',
    score: {
      ideaId: '564',
      scores: { innovation: 8.9, feasibility: 8.5, impact: 9.2, cost: 9.1, security: 8.5 },
      totalScore: 8.8,
      rank: 4,
      reasoning: 'Excels in Impact and Cost with transformative development capabilities'
    },
    difficulty: 'High',
    timeToImplement: '8-12 months',
    impact: 'Transformative',
    tags: ['AI', 'Code Generation', 'Automation', 'Development']
  },
  {
    id: '565',
    title: 'Decentralized Federated Learning Network',
    description: 'Global network for collaborative AI training without centralized data aggregation.',
    category: 'Core Technology',
    score: {
      ideaId: '565',
      scores: { innovation: 8.7, feasibility: 8.9, impact: 8.8, cost: 8.9, security: 8.8 },
      totalScore: 8.8,
      rank: 5,
      reasoning: 'Excels in Feasibility and Cost with strong collaborative AI foundation'
    },
    difficulty: 'High',
    timeToImplement: '12-16 months',
    impact: 'Significant',
    tags: ['Federated Learning', 'Decentralized', 'AI', 'Collaboration']
  },
  {
    id: '566',
    title: 'Real-Time Threat Intelligence Mesh',
    description: 'Distributed security intelligence network that shares threat data in real-time across organizations.',
    category: 'Security & Compliance',
    score: {
      ideaId: '566',
      scores: { innovation: 8.5, feasibility: 9.0, impact: 8.9, cost: 8.7, security: 9.2 },
      totalScore: 8.7,
      rank: 6,
      reasoning: 'Excels in Feasibility and Security with strong threat prevention capabilities'
    },
    difficulty: 'Medium',
    timeToImplement: '8-12 months',
    impact: 'Significant',
    tags: ['Threat Intelligence', 'Security', 'Real-time', 'Collaboration']
  },
  {
    id: '567',
    title: 'Edge AI Optimization Platform',
    description: 'Platform for deploying and optimizing AI models on edge devices with minimal resource consumption.',
    category: 'Core Technology',
    score: {
      ideaId: '567',
      scores: { innovation: 8.3, feasibility: 9.1, impact: 8.7, cost: 9.0, security: 8.4 },
      totalScore: 8.7,
      rank: 7,
      reasoning: 'Excels in Feasibility and Cost with strong edge computing capabilities'
    },
    difficulty: 'Medium',
    timeToImplement: '6-10 months',
    impact: 'Significant',
    tags: ['Edge Computing', 'AI', 'Optimization', 'IoT']
  },
  {
    id: '568',
    title: 'Adaptive Security Architecture',
    description: 'Self-healing security system that adapts to new threats and automatically implements countermeasures.',
    category: 'Security & Compliance',
    score: {
      ideaId: '568',
      scores: { innovation: 8.6, feasibility: 8.4, impact: 8.8, cost: 8.5, security: 9.0 },
      totalScore: 8.6,
      rank: 8,
      reasoning: 'Excels in Innovation and Security with adaptive threat response'
    },
    difficulty: 'High',
    timeToImplement: '10-14 months',
    impact: 'Significant',
    tags: ['Adaptive Security', 'Self-healing', 'Threat Response', 'Automation']
  },
  {
    id: '569',
    title: 'Semantic Data Mesh Platform',
    description: 'Next-generation data architecture that provides semantic understanding and automatic data governance.',
    category: 'Core Technology',
    score: {
      ideaId: '569',
      scores: { innovation: 8.2, feasibility: 8.8, impact: 8.6, cost: 8.8, security: 8.5 },
      totalScore: 8.6,
      rank: 9,
      reasoning: 'Excels in Feasibility and Cost with strong data management capabilities'
    },
    difficulty: 'Medium',
    timeToImplement: '8-12 months',
    impact: 'Significant',
    tags: ['Data Mesh', 'Semantic', 'Governance', 'Architecture']
  },
  {
    id: '570',
    title: 'Autonomous Infrastructure Scaling',
    description: 'AI-driven infrastructure that automatically scales, optimizes, and heals itself based on usage patterns.',
    category: 'Core Technology',
    score: {
      ideaId: '570',
      scores: { innovation: 8.1, feasibility: 8.9, impact: 8.5, cost: 9.0, security: 8.2 },
      totalScore: 8.5,
      rank: 10,
      reasoning: 'Excels in Feasibility and Cost with strong automation capabilities'
    },
    difficulty: 'Medium',
    timeToImplement: '6-9 months',
    impact: 'Significant',
    tags: ['Infrastructure', 'Automation', 'Scaling', 'Self-healing']
  }
];

const IdeaEvaluationPhase: React.FC<IdeaEvaluationPhaseProps> = ({
  ideas,
  onComplete
}) => {
  const [currentStage, setCurrentStage] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [evaluationLogs, setEvaluationLogs] = useState<string[]>([]);
  const [evaluationCriteria, setEvaluationCriteria] = useState<EvaluationCriteria[]>([]);
  const [ideaScores, setIdeaScores] = useState<IdeaScore[]>([]);
  const [tournamentMatches, setTournamentMatches] = useState<TournamentMatch[]>([]);
  const [selectedIdea, setSelectedIdea] = useState<any>(null);
  const [currentMatch, setCurrentMatch] = useState<TournamentMatch | null>(null);
  const [stageProgress, setStageProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [gameRules, setGameRules] = useState<any>(null);
  const [scoringMatrix, setScoringMatrix] = useState<any[]>([]);
  const [idealityScores, setIdealityScores] = useState<any[]>([]);
  const [currentProcessingStep, setCurrentProcessingStep] = useState<string>('');
  const [tournamentBrackets, setTournamentBrackets] = useState<any[]>([]);
  const [liveScoring, setLiveScoring] = useState<any[]>([]);

  const stages = [
    { name: 'Game Rules Creation', duration: 400, progress: 20 },
    { name: 'Tournament Setup', duration: 400, progress: 40 },
    { name: 'Multi-parameter Scoring', duration: 400, progress: 60 },
    { name: 'Tournament Competition', duration: 400, progress: 80 },
    { name: 'Final Ranking & Selection', duration: 400, progress: 100 }
  ];

  const criteria: EvaluationCriteria[] = [
    {
      id: 'innovation',
      name: 'Innovation Level',
      description: 'How novel and creative is the solution?',
      weight: 0.25,
      icon: Lightbulb
    },
    {
      id: 'feasibility',
      name: 'Technical Feasibility',
      description: 'How realistic is the implementation?',
      weight: 0.20,
      icon: Target
    },
    {
      id: 'impact',
      name: 'Business Impact',
      description: 'What is the potential business value?',
      weight: 0.25,
      icon: TrendingUp
    },
    {
      id: 'cost',
      name: 'Cost Effectiveness',
      description: 'What is the cost-benefit ratio?',
      weight: 0.15,
      icon: DollarSign
    },
    {
      id: 'security',
      name: 'Security & Compliance',
      description: 'How well does it address security requirements?',
      weight: 0.15,
      icon: Shield
    }
  ];

  const dataTemplates: any = {
    'AI Enhancement': {
      whyPoints: [
        "**Unlocks New AI Capabilities:** Goes beyond standard models to enable more accurate predictions and automation.",
        "**Data-Driven Decision Making:** Provides deeper insights from existing data, driving significant business value.",
        "**Ethical AI Framework:** Built with fairness and transparency in mind, reducing model bias."
      ],
      features: {
        core: ["**Advanced Model Architecture:** Utilizes a novel neural network structure for superior performance.", "**Real-time Inference Engine:** Capable of processing thousands of requests per second with low latency.", "**Explainability Module (XAI):** Provides clear reasoning for its predictions, building user trust."],
        advanced: ["Self-tuning Hyperparameters", "Federated Learning Support", "Adversarial Attack Resistance"]
      },
      futureVision: "Become the industry standard for trustworthy and high-performance AI, enabling applications previously impossible due to ethical or performance constraints.",
      revenueStreams: ["Tiered SaaS for API access", "Per-prediction enterprise billing", "Consulting for custom model training"],
      comparison: {
        vsQuantum: `**More Immediate Market:** Solves today's AI/ML problems, while quantum is a longer-term play.`,
        vsZeroKnowledge: `**Broader Application:** Applicable to any ML problem, not just verification.`,
        vsFederated: `**Centralized Excellence:** Can achieve higher accuracy with a global model view, assuming data access is possible.`
      }
    },
    'Core Technology': {
       whyPoints: [
        "**Fundamental Infrastructure Improvement:** Re-engineers a core part of the tech stack for 10x performance gains.",
        "**Massive Scalability:** Designed to handle petabyte-scale data and millions of concurrent users.",
        "**Developer Ecosystem Enablement:** Opens up new possibilities for other teams to build upon this platform."
      ],
      features: {
        core: ["**High-Performance Rust/Go Core:** Written in a modern, memory-safe language for maximum speed.", "**Cloud-Native Architecture:** Leverages Kubernetes, serverless, and service mesh for resilience.", "**Extensible Plugin System:** Allows for custom functionality to be added easily."],
        advanced: ["Multi-region Disaster Recovery", "Zero-downtime Deployments", "Advanced Telemetry & Observability"]
      },
      futureVision: "To become the underlying infrastructure for a new generation of high-performance applications, setting the standard for reliability and speed.",
      revenueStreams: ["Open-core model with enterprise licensing", "Managed cloud service offerings", "Professional support and services"],
       comparison: {
        vsQuantum: `**Wider Applicability:** Improves performance across the board, not just for security.`,
        vsZeroKnowledge: `**Platform Play:** Creates a foundation for others, rather than being a single-use-case product.`,
        vsFederated: `**Solves a Different Problem:** Focuses on raw performance and scalability, which can complement federated learning.`
      }
    },
    'Security & Compliance': {
      whyPoints: [
          "**Proactive Threat Mitigation:** Moves from a reactive to a predictive security posture.",
          "**Next-Generation Cryptography:** Implements cutting-edge encryption to protect against future threats.",
          "**Automated Compliance:** Drastically reduces the manual effort required to meet regulatory standards like GDPR and CCPA."
      ],
      features: {
        core: ["**Zero-Trust Policy Engine:** Enforces strict access controls based on dynamic context, not just network location.", "**Confidential Computing Enclave:** Processes sensitive data in a hardware-isolated secure environment.", "**Behavioral Anomaly Detection:** Uses ML to identify suspicious activity that signature-based systems miss."],
        advanced: ["Post-Quantum Cryptography Readiness", "Automated Threat Hunting", "Decentralized Identity Integration"]
      },
      futureVision: "To eliminate entire classes of security vulnerabilities and make robust, automated compliance accessible to all organizations.",
      revenueStreams: ["Per-user security subscription", "Compliance-as-a-service API", "Breach insurance partnerships"],
       comparison: {
        vsQuantum: `**Complementary:** This is the application layer that would run on quantum-safe infrastructure.`,
        vsZeroKnowledge: `**More Comprehensive:** Provides a full security suite, of which ZK could be one feature.`,
        vsFederated: `**Security-First:** Designed with a security threat model from the ground up.`
      }
    },
     'Default': {
      whyPoints: [
        "**Significant Market Opportunity:** Addresses a well-understood and growing market need.",
        "**Strong Competitive Differentiator:** Offers a unique feature set that is difficult for competitors to replicate.",
        "**Clear Path to Monetization:** Has a straightforward business model with proven demand."
      ],
      features: {
        core: ["**Intuitive User Interface:** Simplifies a complex workflow, making it accessible to non-experts.", "**Robust API Layer:** Allows for easy integration into existing customer ecosystems.", "**Advanced Analytics Dashboard:** Provides actionable insights and reporting."],
        advanced: ["Workflow Automation", "Third-party Integrations Marketplace", "AI-powered Recommendations"]
      },
      futureVision: "To become the dominant platform in its niche by providing the best user experience and most powerful feature set.",
      revenueStreams: ["Standard tiered SaaS model", "Usage-based billing", "Professional services for enterprise onboarding"],
       comparison: {
        vsQuantum: `**Lower Technical Risk:** Based on proven technologies, leading to a faster time-to-market.`,
        vsZeroKnowledge: `**Broader Target Audience:** Appeals to a wider user base than a specialized cryptographic tool.`,
        vsFederated: `**Simpler Implementation:** Does not require the complex coordination of multiple data parties.`
      }
    }
  };

  const generateDetailedIdeaData = (idea: any, rank: number, scoreData: any) => {
    // Check if this is the champion idea with detailed data
    if (idea.detailedData) {
      return {
        ...idea,
        score: scoreData,
        ...idea.detailedData
      };
    }

    // ... existing template generation code for other ideas ...
    const score = scoreData.totalScore;
    const marketSize = (score * 2 + Math.random() * 5).toFixed(1);
    const timeToMarket = `${Math.floor(score * 1.5 + Math.random() * 6)} - ${Math.floor(score * 2 + Math.random() * 12)} months`;

    const trizPrinciplesText = idea.trizPrinciples ? `Principle${idea.trizPrinciples.length > 1 ? 's' : ''} ${idea.trizPrinciples.join(', ')}` : 'a relevant TRIZ principle';
    
    const template = dataTemplates[idea.category] || dataTemplates['Default'];

    return {
      ...idea,
      score: scoreData,
      whyThisIsNumberOne: {
        title: `Why This Is The #${rank} Innovation`,
        points: template.whyPoints.map((p: string) => p.replace('$', `$${marketSize}`))
      },
      revolutionaryFeatures: {
          core: template.features.core.map((f: string) => f.replace('${TITLE}', idea.title.split(' ')[0]).replace('${DESC}', idea.description.toLowerCase().slice(0, 30))),
          advanced: template.features.advanced
      },
      futurePotential: {
          fiveYear: [
              `**Healthcare Revolution:** Enable predictive models on sensitive patient data without privacy violations.`,
              `**Financial Transformation:** Global fraud detection without exposing underlying transaction data.`,
              `**Enterprise AI Democratization:** Allow smaller companies to train models on industry-wide datasets securely.`
          ],
          tenYear: [
              `**Regulatory Compliance:** Become the gold standard for AI in regulated industries.`,
              '**Global Collaboration:** Foster international research without data sharing concerns.',
              '**Democratic AI:** Preserve individual privacy while benefiting from collective intelligence.'
          ]
      },
      commercialValue: {
          revenueStreams: template.revenueStreams,
          costSavings: [
              'Reduce compliance and regulatory costs by 50-70%.',
              'Eliminate risks associated with data breaches.',
              'Accelerate model deployment by removing data anonymization steps.'
          ],
          competitiveAdvantages: [
              'First-mover advantage with potentially patentable technology.',
              'Strong network effects as more data improves model accuracy.',
              'High technical barrier to entry for competitors.'
          ]
      },
      innovationHighlights: {
          technical: [
              'Novel algorithms reducing computational overhead by up to 40%.',
              'Parallel processing architectures for encrypted operations.',
              'Optimized circuit designs for common ML models.'
          ],
          industry: [
              'First commercially viable implementation of this technology at scale.',
              'Enables cross-border AI training without data movement.',
              'Built-in compliance with major global privacy laws (GDPR, CCPA).'
          ]
      },
      comparisonToOthers: {
        vsQuantum: template.comparison.vsQuantum,
        vsZeroKnowledge: template.comparison.vsZeroKnowledge,
        vsFederated: template.comparison.vsFederated
      },
      implementationRoadmap: [
        `Phase 1 (Months 1-6): Foundation - Core engine development and proof-of-concept with synthetic data.`,
        `Phase 2 (Months 7-12): Advanced ML - Deep learning support and real-world pilot with an industry partner.`,
        `Phase 3 (Months 13-18): Platform - Full-featured ML platform, API ecosystem, and commercial launch.`
      ],
      technicalDeepDive: {
          architecture: `A microservices architecture is recommended for scalability and independent deployment. ${idea.category === 'AI Enhancement' ? 'An event-driven approach using Kafka or RabbitMQ will handle asynchronous processing of AI models.' : ''}`,
          techStack: {
              frontend: 'React/TypeScript, Tailwind CSS',
              backend: `Go / Rust / Node.js (for performance-critical services)`,
              database: 'PostgreSQL for relational data, Redis for caching',
              infra: 'Kubernetes on AWS/GCP, Terraform for IaC'
          },
          integrationPoints: [
              'Internal authentication service (OAuth 2.0)',
              'External payment gateway (e.g., Stripe)',
              'CRM and support platforms (e.g., Salesforce, Zendesk)'
          ]
      },
      resourceAllocation: {
          team: '1 Product Manager, 2 Senior Backend Engineers, 1 Frontend Engineer, 1 DevOps/SRE, 0.5 UI/UX Designer',
          budgetNote: 'Initial budget should cover 12 months of runway for the core team, plus infrastructure and marketing costs.'
      },
      trizFlow: {
        contradiction: idea.contradiction || `Improving ${idea.tags?.[0] || 'feature'} performance vs. reducing implementation cost.`,
        principle: `Leveraging ${trizPrinciplesText} (${idea.title}) to resolve the core conflict.`,
        implementation: `Develop a modular, scalable architecture that allows for phased implementation, prioritizing core value delivery and adapting to user feedback.`,
        benefit: `Achieves significant performance gains and market differentiation while managing development costs and risks effectively.`
      },
      marketResearch: {
        marketSize: `$${marketSize}B global market by 2028`,
        competitorAnalysis: 'Legacy solutions from established players lack flexibility; new entrants are emerging but currently fragmented.',
        targetSegment: `Enterprises in ${idea.category === 'Core Technology' ? 'multiple sectors' : 'a specific niche'} seeking next-generation, high-impact solutions.`,
        revenueModel: 'Tiered SaaS subscription based on usage, feature set, and level of support.',
        timeToMarket: idea.timeToImplement || timeToMarket,
        riskFactors: ['Potential for new competing technologies to emerge', 'Market adoption rate and user education', 'Integration complexity with existing legacy systems']
      },
      keyPoints: [
        `Addresses a critical, unmet need for the target segment, with potential to disrupt the $${marketSize}B market.`,
        'Offers a strong competitive advantage through the novel application of technology.',
        'Scalable architecture designed for future growth and product line expansion.',
        'High potential for creating a strong IP portfolio and filing for patents.'
      ],
          detailedAnalysis: idea.reasoning || `This idea ranks #${rank} due to its high impact-to-effort ratio, a strong alignment with pressing market needs, and a clear path to monetization. The application of ${trizPrinciplesText} provides a solid foundation for innovation and a sustainable competitive advantage.`
  };
};

  const generateGameRules = () => {
    return {
      scoringMethod: 'Multi-criteria weighted scoring with pairwise comparison',
      eliminationRounds: Math.ceil(Math.log2(ideas.length)),
      winnerSelection: 'Top 10 ideas based on weighted total score',
      tieBreaking: 'Innovation score takes precedence',
      validationCriteria: [
        'Must align with business objectives',
        'Technical feasibility score > 6.0',
        'Innovation score > 7.0',
        'Implementation timeline < 18 months'
      ]
    };
  };

  const scoreIdea = (idea: any, criterion: EvaluationCriteria): number => {
    // Simulate intelligent scoring based on idea attributes
    let baseScore = 0;
    
    switch (criterion.id) {
      case 'innovation':
        baseScore = idea.innovationScore || (7 + Math.random() * 2.5);
        break;
      case 'feasibility':
        baseScore = idea.feasibilityScore || (6 + Math.random() * 3);
        break;
      case 'impact':
        baseScore = idea.impactScore || (6.5 + Math.random() * 3);
        break;
      case 'cost':
        // Higher score for lower cost (cost effectiveness)
        baseScore = 8 - (Math.random() * 2) + (idea.category === 'Core Technology' ? 1 : 0);
        break;
      case 'security':
        baseScore = 7 + Math.random() * 2 + (idea.category === 'Security Innovation' ? 1.5 : 0);
        break;
    }
    
    return Math.min(10, Math.max(1, baseScore));
  };

  const generateTournamentMatches = (ideas: any[]): TournamentMatch[] => {
    const matches: TournamentMatch[] = [];
    const totalRounds = Math.ceil(Math.log2(ideas.length));
    
    // Create first round matches
    for (let i = 0; i < ideas.length - 1; i += 2) {
      if (ideas[i + 1]) {
        matches.push({
          id: `match-${i/2}-1`,
          idea1: ideas[i],
          idea2: ideas[i + 1],
          winner: null,
          criteria: criteria[Math.floor(Math.random() * criteria.length)].id,
          round: 1
        });
      }
    }
    
    return matches;
  };

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  
  const processStageProgressive = async (stageIndex: number, stageFunction: () => Promise<void>) => {
    const stage = stages[stageIndex];
    setCurrentStage(stageIndex);
    
    // Progressive stage processing with streaming progress
    await new Promise(resolve => {
      let progress = stageIndex > 0 ? stages[stageIndex - 1].progress : 0;
      const increment = (stage.progress - progress) / 10;
      const adjustedDuration = stage.duration;
      
      const updateProgress = () => {
        progress += increment;
        setStageProgress(Math.min(progress, stage.progress));
        
        if (progress >= stage.progress) {
          resolve(undefined);
        } else {
          setTimeout(updateProgress, adjustedDuration / 10);
        }
      };
      updateProgress();
    });
    
    // Execute stage function
    await stageFunction();
  };

  const processEvaluationProgressive = async () => {
    const addLog = (message: string) => {
      setEvaluationLogs(prev => [...prev, message]);
    };

    try {
      // Stage 1: Game Rules Creation
      await processStageProgressive(0, async () => {
        setCurrentProcessingStep('Creating game rules from problem definition...');
        addLog('🎯 Stage 1: Game Rules Creation - STARTING');
        addLog('');
        addLog('📋 Analyzing problem view and constraints...');
        
        const rules = generateGameRules();
        setGameRules(rules);
        addLog('✅ Derived evaluation criteria from "Definition of Good" parameters');
        addLog(`✅ Established ${criteria.length} scoring dimensions with weighted importance`);
        addLog('✅ Created tournament elimination structure');
        addLog(`✅ Set validation thresholds: Innovation > 7.0, Feasibility > 6.0`);
        addLog('');
      });

      // Stage 2: Tournament Setup
      await processStageProgressive(1, async () => {
        setCurrentProcessingStep('Setting up tournament brackets...');
        addLog('⚔️ Stage 2: Tournament Setup - STARTING');
        addLog('');
        
        await delay(120);
        
        const brackets = generateTournamentMatches(TOP_10_INNOVATION_IDEAS.slice(0, 16));
        setTournamentBrackets(brackets);
        addLog(`✅ Organized ${TOP_10_INNOVATION_IDEAS.length} ideas into tournament structure`);
        addLog(`✅ Created ${brackets.length} head-to-head comparison matches`);
        addLog('✅ Prepared pairwise evaluation matrix');
        addLog('');
      });

      // Stage 3: Multi-parameter Scoring
      await processStageProgressive(2, async () => {
        setCurrentProcessingStep('Calculating multi-dimensional scores...');
        addLog('📊 Stage 3: Multi-parameter Scoring - STARTING');
        addLog('');
        
        await delay(200);
        
        // Use hardcoded scoring data
        const matrix: Array<{
          ideaId: string;
          title: string;
          scores: Record<string, number>;
          weightedTotal: number;
        }> = [];
        
                 TOP_10_INNOVATION_IDEAS.forEach((idea, index) => {
           addLog(`🔬 Evaluating Idea ${index + 1}: "${idea.title}"`);
           
           const scoreData = idea.score;
           
           criteria.forEach(criterion => {
             const score = (scoreData.scores as any)[criterion.id];
             addLog(`   ${criterion.name}: ${score.toFixed(1)}/10.0 (weight: ${(criterion.weight * 100).toFixed(0)}%)`);
           });
          
          matrix.push({
            ideaId: idea.id,
            title: idea.title,
            scores: scoreData.scores,
            weightedTotal: scoreData.totalScore
          });
          
          addLog(`   💡 Weighted Total: ${scoreData.totalScore.toFixed(2)}/10.0`);
          addLog('');
        });
        
        setScoringMatrix(matrix);
        addLog('✅ Generated comprehensive scoring matrix');
        addLog(`✅ Calculated weighted scores across ${criteria.length} evaluation dimensions`);
        addLog('');
        
        // Store scores for next stage
        (window as any).evaluationScores = TOP_10_INNOVATION_IDEAS.map(idea => idea.score);
      });

      // Stage 4: Tournament Competition & Ideality Scoring
      await processStageProgressive(3, async () => {
        setCurrentProcessingStep('Running tournament competition...');
        addLog('🥇 Stage 4: Tournament Competition & Ideality Scoring - STARTING');
        addLog('');
        
        await delay(160);
        
        const scores = TOP_10_INNOVATION_IDEAS.map(idea => idea.score);
        
        // Calculate ideality scores (TRIZ concept)
        const idealityScoreList = scores.map((score: any) => {
          const benefits = (score.scores.innovation + score.scores.impact) / 2;
          const costs = (10 - score.scores.cost) / 10;
          const harm = (10 - score.scores.security) / 10;
          const idealityScore = benefits / (costs + harm + 0.1);
          
          return {
            ideaId: score.ideaId,
            benefits,
            costs,
            harm,
            idealityScore,
            totalScore: score.totalScore
          };
        });
        
        setIdealityScores(idealityScoreList);
        addLog('🔥 Calculated TRIZ Ideality Scores (Benefits / (Cost + Harm))');
        idealityScoreList.slice(0, 5).forEach((idea: any, index: any) => {
          const ideaTitle = TOP_10_INNOVATION_IDEAS.find(i => i.id === idea.ideaId)?.title || 'Unknown';
          addLog(`   ${index + 1}. ${ideaTitle}: Ideality = ${idea.idealityScore.toFixed(2)}`);
        });
        addLog('');
        
        // Run tournament matches
        const brackets = tournamentBrackets;
        addLog('⚡ Executing head-to-head tournament matches...');
        brackets.forEach((match, index) => {
          const score1 = scores.find((s: any) => s.ideaId === match.idea1.id)?.totalScore || 0;
          const score2 = scores.find((s: any) => s.ideaId === match.idea2.id)?.totalScore || 0;
          const winner = score1 > score2 ? match.idea1.title : match.idea2.title;
          addLog(`   Match ${index + 1}: ${match.idea1.title} vs ${match.idea2.title} → Winner: ${winner}`);
        });
        
        addLog('');
        
        // Store scores for final stage
        (window as any).finalScores = scores;
      });

      // Stage 5: Final Ranking & Selection
      await processStageProgressive(4, async () => {
        setCurrentProcessingStep('Generating final rankings...');
        addLog('🎖️ Stage 5: Final Ranking & Top 10 Selection - STARTING');
        addLog('');
        
        await delay(120);
        
        const scores = TOP_10_INNOVATION_IDEAS.map(idea => idea.score);
        
        addLog('🏆 FINAL RANKINGS - TOP 10 BREAKTHROUGH IDEAS:');
        addLog('');
        scores.slice(0, 10).forEach((score: any, index: number) => {
          const idea = TOP_10_INNOVATION_IDEAS.find(i => i.id === score.ideaId);
          const medal = index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : '🏅';
          addLog(`${medal} #${index + 1} - ${idea?.title} (Score: ${score.totalScore.toFixed(2)})`);
          addLog(`     Innovation: ${score.scores.innovation?.toFixed(1)}, Feasibility: ${score.scores.feasibility?.toFixed(1)}, Impact: ${score.scores.impact?.toFixed(1)}`);
        });
        
        addLog('');
        addLog('✅ Automatically bubbled up top 10 ideas without manual intervention');
        addLog('✅ Generated executive recommendation matrix');
        addLog('✅ Created detailed justification for each ranking');
        addLog('');
        addLog('🎉 Tournament-style evaluation completed successfully!');

        setEvaluationCriteria(criteria);
        setIdeaScores(scores);
        setTournamentMatches(tournamentBrackets);
        setCurrentProcessingStep('Evaluation complete!');
      });
      
      // Final completion
      setIsProcessing(false);
      setIsCompleted(true);
      
    } catch (error) {
      addLog('❌ Error during evaluation process');
      setIsProcessing(false);
    }
  };

  const handleComplete = () => {
    const top10Ideas = TOP_10_INNOVATION_IDEAS.slice(0, 10);
    
    onComplete({
      topIdeas: top10Ideas.map(idea => idea.score),
      evaluationCriteria: criteria,
      fullResults: TOP_10_INNOVATION_IDEAS.map(idea => idea.score),
      tournamentResults: tournamentMatches,
      summary: {
        totalEvaluated: TOP_10_INNOVATION_IDEAS.length,
        selectedIdeas: 10,
        avgTopScore: top10Ideas.reduce((sum, idea) => sum + idea.score.totalScore, 0) / top10Ideas.length,
        winnerCategory: TOP_10_INNOVATION_IDEAS[0]?.category || 'AI Enhancement',
        evaluationInsights: [
          'Privacy-preserving AI innovations scored highest overall',
          'Security & Compliance solutions showed strong market demand',
          'Core Technology solutions demonstrated best feasibility scores',
          'AI Enhancement innovations offer highest transformation potential'
        ]
      }
    });
  };

  const startEvaluation = () => {
    setIsProcessing(true);
    setEvaluationLogs(['🎯 Initializing Idea Evaluation Engine...']);
    setStageProgress(0);
    setCurrentStage(0);
    
    // Start progressive processing
    processEvaluationProgressive();
  };

  const getRankColor = (rank: number) => {
    if (rank === 1) return 'from-yellow-400 to-yellow-600';
    if (rank <= 3) return 'from-slate-300 to-slate-500';
    if (rank <= 10) return 'from-orange-400 to-orange-600';
    return 'from-slate-600 to-slate-800';
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return Crown;
    if (rank <= 3) return Medal;
    if (rank <= 10) return Award;
    return Star;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }}>
      <div className="max-w-7xl mx-auto p-8 space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="w-16 h-16 sqsft-gradient-primary rounded-3xl flex items-center justify-center shadow-2xl">
              <Trophy className="w-8 h-8 text-white" />
          </div>
          <div>
              <h2 className="text-4xl lg:text-5xl sqsft-heading-1 mb-2">
                <span className="sqsft-gradient-text">Idea Evaluation</span>
              </h2>
              <p className="text-lg sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                Systematic idea analysis using a multi-criteria decision framework.
              </p>
          </div>
        </div>
        
        {!isProcessing && !isCompleted && (
          <div className="space-y-4">
            
            <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.98 }}
              onClick={startEvaluation}
                className="sqsft-btn-primary px-6 py-3"
            >
              Start Evaluation
            </motion.button>
          </div>
        )}
        
        {isCompleted && (
          <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
            onClick={handleComplete}
              className="sqsft-btn-primary px-6 py-3"
          >
            Complete Phase
          </motion.button>
        )}
      </div>

      {/* Processing Status */}
      {isProcessing && (
          <div className="sqsft-feature-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="sqsft-heading-4">
                Stage {currentStage + 1}/5: {stages[currentStage]?.name}
              </div>
                <div className="sqsft-caption mt-1" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                  {currentProcessingStep}
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <span className="font-bold text-xl sqsft-gradient-text">
                  {stageProgress.toFixed(0)}%
                </span>
              </div>
            </div>
            <div className="w-full rounded-full h-3" style={{ backgroundColor: 'rgb(var(--color-border))' }}>
            <motion.div
                className="h-3 rounded-full sqsft-gradient-primary shadow-lg"
              initial={{ width: 0 }}
              animate={{ width: `${stageProgress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
      )}

      {/* Completion Status */}
      {isCompleted && !isProcessing && (
          <div className="sqsft-feature-card p-6 border-2" style={{ borderColor: 'rgb(var(--color-sage-green))', backgroundColor: 'rgba(66, 102, 83, 0.05)' }}>
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 sqsft-gradient-primary rounded-2xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="sqsft-heading-3">
                  <span className="sqsft-gradient-text">Evaluation Complete!</span>
                </h3>
                <p className="sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                  Evaluated {TOP_10_INNOVATION_IDEAS.length} ideas and ranked top {Math.min(10, ideaScores.length)} solutions. Ready to proceed to the next phase.
                </p>
              </div>
            </div>
        </div>
      )}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Evaluation Logs */}
          <div className="sqsft-feature-card p-6">
          <h3 className="sqsft-heading-3 mb-4">
            <span className="sqsft-gradient-text">Live Evaluation Process</span>
          </h3>
            <div className="rounded-xl p-4 h-96 overflow-y-auto font-mono text-sm" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }}>
            {evaluationLogs.length > 0 ? (
              evaluationLogs.map((log, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                  className={`${
                    log.startsWith('✅')
                      ? 'text-green-600'
                      : log.startsWith('🎯') || log.startsWith('🎉') || log.startsWith('🏆')
                      ? 'sqsft-gradient-text'
                      : log.startsWith('⚔️') || log.startsWith('📊') || log.startsWith('🥇') || log.startsWith('🎖️')
                      ? 'sqsft-gradient-text'
                      : log.startsWith('🔬') || log.startsWith('🔥') || log.startsWith('⚡')
                      ? 'text-purple-600'
                      : log.startsWith('🥇') || log.startsWith('🥈') || log.startsWith('🥉') || log.startsWith('🏅')
                      ? 'sqsft-gradient-text-gold'
                      : 'text-slate-700'
                  } leading-relaxed`}
              >
                {log}
              </motion.div>
            ))
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-16 h-16 sqsft-gradient-primary rounded-2xl flex items-center justify-center mb-4">
                  <Cpu className="w-8 h-8 text-white" />
                </div>
                <h4 className="sqsft-heading-4 mb-2">Evaluation Engine</h4>
                <p className="sqsft-body max-w-xs" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                  The live evaluation process will be shown here once initiated. Click "Start Evaluation" to begin.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Game Rules & Evaluation Criteria */}
          <div className="sqsft-feature-card p-6">
          <h3 className="sqsft-heading-3 mb-4">
            <span className="sqsft-gradient-text">Evaluation Framework</span>
          </h3>
            <div className="h-96 overflow-y-auto pr-2">
            {gameRules && (
              <div className="mb-6">
                  <h4 className="sqsft-heading-4 mb-3">Evaluation Protocol</h4>
                  <div className="rounded-xl p-4 border-2 mb-4" style={{ backgroundColor: 'rgba(66, 102, 83, 0.05)', borderColor: 'rgb(var(--color-sage-green))' }}>
                    <div className="space-y-2 text-sm sqsft-body text-slate-700">
                      <div>
                        <strong style={{ color: 'rgb(var(--color-deep-green))' }}>Scoring Method:</strong> {gameRules.scoringMethod}
                  </div>
                      <div>
                        <strong style={{ color: 'rgb(var(--color-deep-green))' }}>Elimination Rounds:</strong> {gameRules.eliminationRounds}
                </div>
                      <div>
                        <strong style={{ color: 'rgb(var(--color-deep-green))' }}>Winner Selection:</strong> {gameRules.winnerSelection}
                      </div>
                      <div>
                        <strong style={{ color: 'rgb(var(--color-deep-green))' }}>Tie Breaking:</strong> {gameRules.tieBreaking}
                      </div>
                    </div>
                </div>
              </div>
            )}
            
            {(evaluationCriteria.length > 0 || !gameRules) && (
              <div>
                  <h4 className="sqsft-heading-4 mb-3">{!gameRules ? 'Core Evaluation Criteria' : 'Weighted Evaluation Criteria'}</h4>
                <div className="space-y-3">
                  {(gameRules ? evaluationCriteria : criteria).map((criterion, index) => (
                    <motion.div
                      key={criterion.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                        className="sqsft-feature-card p-4 border hover:border-sage-green transition-all duration-300"
                    >
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 sqsft-gradient-primary rounded-2xl flex items-center justify-center">
                            <criterion.icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                            <h5 className="sqsft-heading-4">{criterion.name}</h5>
                            <p className="sqsft-caption" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                              {criterion.description}
                            </p>
                        </div>
                        
                        {/* Show weight only if game rules are generated */}
                        {gameRules && (
                          <div className="text-center">
                              <div className="font-bold text-lg sqsft-gradient-text">
                                {(criterion.weight * 100).toFixed(0)}%
                              </div>
                            <div className="sqsft-caption" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                              Weight
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
            
              {!gameRules && (
                <div className="mt-6">
                  <h4 className="sqsft-heading-4 mb-3 text-slate-500">Evaluation Protocol (Awaiting Generation)</h4>
                  <div className="bg-slate-50/80 rounded-xl p-4 border border-slate-200/80 space-y-2 text-sm text-slate-500">
                    <div><strong className="text-slate-600">Scoring Method:</strong> Will be defined upon starting...</div>
                    <div><strong className="text-slate-600">Elimination Rounds:</strong> Will be calculated based on idea count...</div>
                    <div><strong className="text-slate-600">Winner Selection:</strong> Top ideas from scoring...</div>
                    <div><strong className="text-slate-600">Tie Breaking:</strong> Precedence rules to be set...</div>
                  </div>
                </div>
            )}
          </div>
        </div>
      </div>

        {/* Results Rankings */}
      <AnimatePresence>
        {ideaScores.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
              className="space-y-8"
          >
            {/* Tournament Winner Spotlight */}
              <div className="group relative">
                 <div className="absolute -inset-2 sqsft-gradient-gold rounded-3xl opacity-0 group-hover:opacity-40 blur-2xl transition-all duration-500 group-hover:blur-3xl"></div>
                 <div className="relative sqsft-feature-card p-8 border-2 transition-all duration-300 group-hover:border-transparent group-hover:shadow-2xl group-hover:scale-[1.01] group-hover:-translate-y-1" style={{ borderColor: 'rgb(var(--color-royal-gold))' }}>
                  <div className="flex items-center space-x-6 mb-6">
                    <div className="w-16 h-16 sqsft-gradient-gold rounded-3xl flex items-center justify-center shadow-2xl">
                      <Crown className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="sqsft-heading-2">
                        <span className="sqsft-gradient-text-gold">Tournament Champion</span>
                      </h3>
                      <p className="sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                        Highest-scoring innovation breakthrough
                      </p>
                    </div>
                  </div>
                  
                  {(() => {
                    const championIdea = TOP_10_INNOVATION_IDEAS[0];
                    if (!championIdea) return null;
                    
                    return (
                        <div className="rounded-xl p-6" style={{ backgroundColor: 'rgba(212, 175, 55, 0.05)' }}>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                          <div className="lg:col-span-2">
                               <h3 className="sqsft-heading-3 flex items-center space-x-2 mb-4">
                                 <div className="w-6 h-6 sqsft-gradient-gold rounded-lg flex items-center justify-center">
                                   <Trophy className="w-4 h-4 text-white" />
                                 </div>
                                 <span className="sqsft-gradient-text">{championIdea.title}</span>
                               </h3>
                               <p className="sqsft-body mb-4" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                                 {championIdea.description}
                               </p>
                               
                               <div className="mt-4">
                                 <h4 className="sqsft-heading-4 mb-2 flex items-center space-x-2">
                                    <div className="w-5 h-5 sqsft-gradient-primary rounded-lg flex items-center justify-center">
                                      <Rocket className="w-3 h-3 text-white" />
                                    </div>
                                    <span className="sqsft-gradient-text">Why This Is The #1 Privacy Innovation</span>
                                  </h4>
                                 <div className="space-y-2 sqsft-body">
                                    <p dangerouslySetInnerHTML={{ __html: '**Breakthrough Technology Convergence:** Combines homomorphic encryption with machine learning to achieve what was previously impossible.'.replace(/\*\*(.*?)\*\*/g, '<strong style="color: rgb(var(--color-deep-green))">$1</strong>') }}></p>
                                    <p dangerouslySetInnerHTML={{ __html: '**Massive Market Potential:** Unlocks AI in regulated industries like healthcare and finance, a $100B+ opportunity.'.replace(/\*\*(.*?)\*\*/g, '<strong style="color: rgb(var(--color-deep-green))">$1</strong>') }}></p>
                                  </div>
                                </div>
                          </div>
                          
                          <div className="space-y-4">
                               <h5 className="sqsft-heading-4">Strategic Overview</h5>
                               <div className="space-y-3">
                                 <div className="flex items-center space-x-2 sqsft-body">
                                   <Clock className="w-4 h-4" style={{ color: 'rgb(var(--color-sage-green))' }} />
                                   <span style={{ color: 'rgb(var(--color-text-secondary))' }}>Time to Implement:</span>
                                   <span className="font-bold sqsft-gradient-text">{championIdea.timeToImplement}</span>
                                </div>
                                 <div className="flex items-center space-x-2 sqsft-body">
                                   <TrendingUp className="w-4 h-4" style={{ color: 'rgb(var(--color-sage-green))' }} />
                                   <span style={{ color: 'rgb(var(--color-text-secondary))' }}>Impact:</span>
                                   <span className="font-bold sqsft-gradient-text">{championIdea.impact}</span>
                                 </div>
                                 <div className="flex items-center space-x-2 sqsft-body">
                                   <Target className="w-4 h-4" style={{ color: 'rgb(var(--color-sage-green))' }} />
                                   <span style={{ color: 'rgb(var(--color-text-secondary))' }}>Difficulty:</span>
                                   <span className="font-bold sqsft-gradient-text">{championIdea.difficulty}</span>
                                 </div>
                               </div>
                          </div>
                        </div>
                        <div className="mt-6 text-right">
                          <button
                            onClick={() => {
                              const detailedIdea = generateDetailedIdeaData(championIdea, 1, championIdea.score);
                              setSelectedIdea(detailedIdea);
                            }}
                            className="sqsft-btn-primary inline-flex items-center group"
                          >
                            View Full Analysis
                            <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                  </div>
                  
                {/* Rest of the winners */}
                <div className="sqsft-feature-card p-8">
                  <h3 className="sqsft-heading-3 mb-6">
                    <span className="sqsft-gradient-text">Top 10 Innovation Ideas</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {TOP_10_INNOVATION_IDEAS.slice(1, 10).map((idea, index) => {
                    const scoreData = idea.score;
                    
                    const RankIcon = getRankIcon(scoreData.rank);
                    
                    return (
                      <motion.div
                        key={idea.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="sqsft-feature-card p-4 h-full flex flex-col border hover:border-sage-green transition-all duration-300"
                        >
                          <div className="flex items-center space-x-3 mb-3">
                            <div className="w-8 h-8 sqsft-gradient-primary rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                              <RankIcon className="w-4 h-4 text-white" />
                            </div>
                            <h4 className="sqsft-heading-4 flex-1">{idea.title}</h4>
                        </div>
                          <p className="sqsft-body mb-4 flex-grow" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                            {idea.description}
                          </p>
                          <div className="flex items-center justify-end">
                            <button
                              onClick={() => {
                                const detailedIdea = generateDetailedIdeaData(idea, scoreData.rank, scoreData);
                                setSelectedIdea(detailedIdea);
                              }}
                              className="sqsft-btn-secondary text-sm px-4 py-2"
                            >
                              View Details
                            </button>
                              </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
      </AnimatePresence>

        {/* Modal */}
      <AnimatePresence>
        {selectedIdea && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelectedIdea(null)}
          >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="sqsft-feature-card rounded-3xl p-8 max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
                                {/* Modal Header */}
              <div className="flex items-start justify-between mb-8">
                <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 sqsft-gradient-primary rounded-3xl flex items-center justify-center shadow-2xl">
                      <Lightbulb className="w-8 h-8 text-white" />
                  </div>
                  <div>
                      <h2 className="sqsft-heading-1 mb-2">
                        <span className="sqsft-gradient-text">{selectedIdea.title}</span>
                      </h2>
                      <p className="sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                        Innovation Analysis & Strategic Overview
                      </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedIdea(null)}
                    className="sqsft-btn-ghost w-10 h-10 rounded-full flex items-center justify-center text-2xl hover:bg-slate-100"
                >
                    &times;
                </button>
              </div>
              
                <div className="space-y-8">
                  {/* Overview Section */}
                  <div className="rounded-xl p-6" style={{ backgroundColor: 'rgba(66, 102, 83, 0.05)' }}>
                    <p className="sqsft-heading-4 leading-relaxed" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                      {selectedIdea.description}
                    </p>
                  </div>

                  <div className="w-full h-px" style={{ backgroundColor: 'rgb(var(--color-border))' }}></div>
                  
                  {/* The Innovation Section */}
                  <div className="space-y-6">
                    <h3 className="sqsft-heading-2 flex items-center space-x-3">
                      <div className="w-8 h-8 sqsft-gradient-primary rounded-2xl flex items-center justify-center">
                        <Rocket className="w-5 h-5 text-white" />
                      </div>
                      <span className="sqsft-gradient-text">The Innovation</span>
                    </h3>
                    
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                           <h4 className="sqsft-heading-4 mb-3">{selectedIdea.whyThisIsNumberOne.title}</h4>
                           <div className="space-y-3">
                            {selectedIdea.whyThisIsNumberOne.points.map((point: string, idx: number) => (
                              <p key={idx} className="text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: point.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}></p>
                            ))}
                          </div>
                        </div>
                        <div>
                           <h4 className="sqsft-heading-4 mb-3">Revolutionary Features</h4>
                            <div>
                              <h5 className="font-semibold text-sm text-slate-600 mb-2">Core Features:</h5>
                               <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                                  {selectedIdea.revolutionaryFeatures.core.map((feature: string, idx: number) => <li key={idx} dangerouslySetInnerHTML={{ __html: feature.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}></li>)}
                               </ul>
                            </div>
                            <div className="mt-3">
                               <h5 className="font-semibold text-sm text-slate-600 mb-2">Advanced Capabilities:</h5>
                               <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                                  {selectedIdea.revolutionaryFeatures.advanced.map((feature: string, idx: number) => <li key={idx}>{feature}</li>)}
                               </ul>
                            </div>
                        </div>
                     </div>
                     {/* TRIZ Flow Analysis */}
                    <div className="mt-6">
                        <h4 className="sqsft-heading-4 mb-3 flex items-center space-x-2">
                            <Brain className="w-5 h-5 text-purple-600" />
                            <span>TRIZ Flow Analysis</span>
                        </h4>
                        <div className="bg-purple-50/80 rounded-xl p-4 border border-purple-200/80 space-y-3">
                            <div>
                                <span className="text-xs font-medium text-purple-700">Contradiction:</span>
                                <p className="text-sm text-slate-700">{selectedIdea.trizFlow.contradiction}</p>
                            </div>
                            <div>
                                <span className="text-xs font-medium text-purple-700">TRIZ Principle:</span>
                                <p className="text-sm text-slate-700">{selectedIdea.trizFlow.principle}</p>
                            </div>
                            <div>
                                <span className="text-xs font-medium text-purple-700">Implementation:</span>
                                <p className="text-sm text-slate-700">{selectedIdea.trizFlow.implementation}</p>
                            </div>
                            <div>
                                <span className="text-xs font-medium text-purple-700">Benefit:</span>
                                <p className="text-sm text-slate-700">{selectedIdea.trizFlow.benefit}</p>
                            </div>
                        </div>
                    </div>
                  </div>

                  <div className="w-full h-px" style={{ backgroundColor: 'rgb(var(--color-border))' }}></div>

                  {/* Execution Plan Section */}
                   <div className="space-y-6">
                    <h3 className="sqsft-heading-2 flex items-center space-x-3">
                      <div className="w-8 h-8 sqsft-gradient-gold rounded-2xl flex items-center justify-center">
                        <ClipboardList className="w-5 h-5 text-white" />
                      </div>
                      <span className="sqsft-gradient-text-gold">Execution Plan</span>
                    </h3>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <h4 className="sqsft-heading-4 mb-3">Implementation Roadmap</h4>
                          <div className="space-y-3">
                            {selectedIdea.implementationRoadmap.map((step: string, index: number) => (
                              <div key={index} className="flex items-start space-x-3">
                                <div className="w-6 h-6 sqsft-gradient-primary text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                                  {index + 1}
                                </div>
                                <span className="sqsft-body text-sm text-slate-700">{step}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                           <h4 className="sqsft-heading-4 mb-3">Technical & Resource Overview</h4>
                           <div className="space-y-4">
                              <div>
                                <h5 className="font-semibold text-sm text-slate-600 mb-1 flex items-center space-x-2"><Cpu className="w-4 h-4" /><span>Proposed Architecture</span></h5>
                                <p className="text-sm text-slate-700">{selectedIdea.technicalDeepDive.architecture}</p>
                              </div>
                              <div>
                                <h5 className="font-semibold text-sm text-slate-600 mb-1 flex items-center space-x-2"><Users className="w-4 h-4" /><span>Team Estimate</span></h5>
                                <p className="text-sm text-slate-700">{selectedIdea.resourceAllocation.team}</p>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div className="w-full h-px" style={{ backgroundColor: 'rgb(var(--color-border))' }}></div>
                  
                   {/* Business Case Section */}
                   <div className="space-y-6">
                    <h3 className="sqsft-heading-2 flex items-center space-x-3">
                      <div className="w-8 h-8 sqsft-gradient-primary rounded-2xl flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-white" />
                      </div>
                      <span className="sqsft-gradient-text">Business & Market Case</span>
                    </h3>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <h4 className="sqsft-heading-4 mb-3">Future Potential</h4>
                           <div className="space-y-3">
                              <div>
                                <h5 className="font-semibold text-sm text-slate-600 mb-2">5-Year Vision:</h5>
                                 <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                                    {selectedIdea.futurePotential.fiveYear.map((item: string, idx: number) => <li key={idx} dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}></li>)}
                                 </ul>
                              </div>
                              <div>
                                 <h5 className="font-semibold text-sm text-slate-600 mb-2">10-Year Vision:</h5>
                                 <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                                    {selectedIdea.futurePotential.tenYear.map((item: string, idx: number) => <li key={idx}>{item}</li>)}
                                 </ul>
                              </div>
                           </div>
                        </div>
                        <div>
                           <h4 className="sqsft-heading-4 mb-3">Commercial Value</h4>
                           <div className="space-y-3">
                               <div>
                                 <h5 className="font-semibold text-sm text-slate-600 mb-2">Revenue Streams:</h5>
                                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                                     {selectedIdea.commercialValue.revenueStreams.map((item: string, idx: number) => <li key={idx}>{item}</li>)}
                                  </ul>
                               </div>
                               <div>
                                 <h5 className="font-semibold text-sm text-slate-600 mb-2">Customer Cost Savings:</h5>
                                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                                     {selectedIdea.commercialValue.costSavings.map((item: string, idx: number) => <li key={idx}>{item}</li>)}
                                  </ul>
                               </div>
                           </div>
                        </div>

                     </div>
                      {/* Market Research, Key Points, Risks */}
                    <div className="mt-8">
                        {/* Market Research */}
                        <div>
                            <h4 className="sqsft-heading-4 mb-3 flex items-center space-x-2">
                                <BarChart3 className="w-5 h-5 text-green-600" />
                                <span>Market Research & Potential</span>
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-green-50/80 rounded-lg p-4 border border-green-200/80">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <DollarSign className="w-4 h-4 text-green-600" />
                                        <span className="text-xs font-medium text-green-700">Market Size</span>
                                    </div>
                                    <p className="text-sm text-slate-700">{selectedIdea.marketResearch.marketSize}</p>
                                </div>
                                <div className="bg-blue-50/80 rounded-lg p-4 border border-blue-200/80">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <Users className="w-4 h-4 text-blue-600" />
                                        <span className="text-xs font-medium text-blue-700">Target Segment</span>
                                    </div>
                                    <p className="text-sm text-slate-700">{selectedIdea.marketResearch.targetSegment}</p>
                                </div>
                                <div className="bg-purple-50/80 rounded-lg p-4 border border-purple-200/80">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <Eye className="w-4 h-4 text-purple-600" />
                                        <span className="text-xs font-medium text-purple-700">Competition</span>
                                    </div>
                                    <p className="text-sm text-slate-700">{selectedIdea.marketResearch.competitorAnalysis}</p>
                                </div>
                                <div className="bg-orange-50/80 rounded-lg p-4 border border-orange-200/80">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <Clock className="w-4 h-4 text-orange-600" />
                                        <span className="text-xs font-medium text-orange-700">Time to Market</span>
                                    </div>
                                    <p className="text-sm text-slate-700">{selectedIdea.marketResearch.timeToMarket}</p>
                                </div>
                            </div>
                            <div className="mt-4 bg-yellow-50/80 rounded-lg p-4 border border-yellow-200/80">
                                <div className="flex items-center space-x-2 mb-2">
                                    <DollarSign className="w-4 h-4 text-yellow-600" />
                                    <span className="text-xs font-medium text-yellow-700">Revenue Model</span>
                                </div>
                                <p className="text-sm text-slate-700">{selectedIdea.marketResearch.revenueModel}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
                            {/* Key Points */}
                            <div>
                                <h4 className="sqsft-heading-4 mb-3 flex items-center space-x-2">
                                    <CheckCircle className="w-5 h-5 text-blue-600" />
                                    <span>Key Value Points</span>
                                </h4>
                                <div className="space-y-2">
                                    {selectedIdea.keyPoints.map((point: string, idx: number) => (
                                        <div key={idx} className="flex items-start space-x-2">
                                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                            <span className="text-sm text-slate-700">{point}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Risk Factors */}
                            <div>
                                <h4 className="sqsft-heading-4 mb-3 flex items-center space-x-2">
                                    <AlertTriangle className="w-5 h-5 text-red-600" />
                                    <span>Risk Factors</span>
                                </h4>
                                <div className="space-y-2">
                                    {selectedIdea.marketResearch.riskFactors.map((risk: string, idx: number) => (
                                        <div key={idx} className="flex items-start space-x-2">
                                            <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                                            <span className="text-sm text-slate-700">{risk}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                  </div>
                </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default IdeaEvaluationPhase;